import { Instance, types } from 'mobx-state-tree'
import { nanoid } from 'nanoid'
import { ForDirectExtend } from '../../../../features/mobx'

export interface NoteUpdateType {
  start?: number
  duration?: number
  note?: number
  velocity?: number
}

// A single note instance
const _NoteStore = types
  .model({
    uniqueId: '',
    start: 0, // measured in ticks
    duration: 2, // measured in ticks
    note: 60, // 0 (C0) - 127 (G10), inclusive, where middle C is 60
    velocity: 127 // 1 - 127, inclusive
  })
  .views((self) => ({
    get end(): number {
      return self.start + self.duration
    }
  }))
  .actions((self) => {
    function afterCreate() {
      generateID()
    }

    function initialize(values: NoteUpdateType): string {
      generateID()
      update(values)
      return self.uniqueId
    }

    function generateID() {
      self.uniqueId = nanoid()
    }

    function transposeBy(offset: number): boolean {
      return updateNote(self.note + offset)
    }

    ////////////// Setters ////////////////////

    function updateStart(newStart: number): boolean {
      if (newStart < 0) return false
      self.start = Math.round(newStart)
      return true
    }
    function updateDuration(newDuration: number): boolean {
      if (newDuration < 1) return false
      self.duration = Math.round(newDuration)
      return true
    }
    function updateNote(newNote: number): boolean {
      if (newNote < 0 || newNote > 127) return false
      self.note = Math.round(newNote)
      return true
    }
    function updateVelocity(newVelocity: number): boolean {
      if (newVelocity < 1 || newVelocity > 127) return false
      self.velocity = Math.round(newVelocity)
      return true
    }

    function update(newValues: NoteUpdateType) {
      if (newValues.start !== undefined && newValues.start !== null) {
        updateStart(newValues.start)
      }
      if (newValues.duration !== undefined && newValues.duration !== null) {
        updateDuration(newValues.duration)
      }
      if (newValues.note !== undefined && newValues.note !== null) {
        updateNote(newValues.note)
      }
      if (newValues.velocity !== undefined && newValues.velocity !== null) {
        updateVelocity(newValues.velocity)
      }
    }

    ///////////////////////////////////////////

    return {
      afterCreate,
      generateID,
      transposeBy,
      update,
      updateStart,
      updateDuration,
      updateNote,
      updateVelocity,
      initialize
    }
  })

interface NoteStoreType extends ForDirectExtend<typeof _NoteStore> {}
export const NoteStore: NoteStoreType = _NoteStore

export interface INoteStore extends ForDirectExtend<Instance<typeof NoteStore>> {}
